import {
  createContext,
  memo,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useScale from './hooks/useScale';
import { AllStyleProp } from '../types/others';
import Utils from '../utils/utils';
import { Platform } from 'react-native';

class StylesGenerator {
  private scale: number;
  private fontScale: number;

  private styles = new Map<string, AllStyleProp>();

  constructor(scale: number, fontScale: number) {
    this.scale = scale;
    this.fontScale = fontScale;
  }

  /**
   * Получить или создать и получить стиль из краткого названия \
   * Свойства разделяются через '_'. Название и значение через '.'. \
   * Возможные свойства перечислены в объектах {@link propNames},
   * {@link scaledPropNames}, {@link stringPropNames},
   * {@link notScaledPropNames}, {@link objProps}.
   * @example
   * ```tsx
   * styles.getStyle('fs.32_fw.500_c.black')
   * ```
   * @param name Краткое название стиля
   * @returns Объект стиля
   */
  getStyle(name: string): AllStyleProp {
    let result: any = this.styles.get(name);
    if (result) return result;

    result = {};
    const props = name.toLowerCase().split(',');
    for (const p of props) {
      if (p in objProps) {
        result = {
          ...result,
          ...objProps[p],
        };
        continue;
      }

      const [n, v] = p.split('=');
      if (v === undefined) {
        if (__DEV__) console.warn('Wrong style prop', p);
        continue;
      }

      if (n in stringPropNames) {
        result[stringPropNames[n]] = v;
        if (n === 'fw') {
          result['fontFamily'] = 'Inter';
          if (Platform.OS === 'web') result['lineHeight'] = '1.2';
        }
        continue;
      }

      if ((n === 'w' || n === 'h') && v.endsWith('%')) {
        result[propNames[n]] = v;
        continue;
      }

      let value = +v;
      if (isNaN(value)) {
        if (__DEV__) console.warn('Detect NaN', p);
        continue;
      }

      if (n in notScaledPropNames) {
        result[notScaledPropNames[n]] = value;
        continue;
      }

      value /= this.scale;
      if (n in propNames) result[propNames[n]] = value * this.fontScale;
      else if (n in scaledPropNames) result[scaledPropNames[n]] = value;
    }

    if (__DEV__) {
      for (const [key, value] of this.styles) {
        if (Utils.shallowCompare(result, value))
          console.warn('Detect same styles with different key:', name, key);
      }
    }
    this.styles.set(name, result);
    return result;
  }
}

const GlobalStyleContext = createContext<StylesGenerator>(
  new StylesGenerator(1, 1),
);

export const GlobalStyleWrapper = memo<PropsWithChildren>(({ children }) => {
  const scales = useScale();
  const [styles, setStyles] = useState(
    () => new StylesGenerator(scales[0], scales[1]),
  );

  useEffect(() => {
    setStyles(new StylesGenerator(scales[0], scales[1]));
  }, [scales]);

  return (
    <GlobalStyleContext.Provider value={styles}>
      {children}
    </GlobalStyleContext.Provider>
  );
});

/**
 * Получить из контекста глобальные стили из их кратких названий
 * @example
 * ```tsx
 * const styles = ['fs=40,fw=700', 'fs=32,mr=12', 'fill,p=20'];
 * const [s1, s2, s3] = useGlobalStyles();
 * ```
 * @param names Массив наименований стилей для получения
 * @returns Мемоизированный массив стилей
 */
export default function useGlobalStyles(names: string[]) {
  const styles = useContext(GlobalStyleContext);
  const result = useMemo(
    () => names.map(e => styles.getStyle(e)),
    [styles, names],
  );
  return result;
}

type NamesType = { [key: string]: string };

const propNames: NamesType = {
  fs: 'fontSize',
  m: 'margin',
  mh: 'marginHorizontal',
  mv: 'marginVertical',
  mt: 'marginTop',
  mb: 'marginBottom',
  ml: 'marginLeft',
  mr: 'marginRight',
  p: 'padding',
  ph: 'paddingHorizontal',
  pv: 'paddingVertical',
  pt: 'paddingTop',
  pb: 'paddingBottom',
  pl: 'paddingLeft',
  pr: 'paddingRight',
  bw: 'borderWidth',
  w: 'width',
  h: 'height',
};
const scaledPropNames: NamesType = {
  br: 'borderRadius',
};
const notScaledPropNames: NamesType = {
  f: 'flex',
  o: 'opacity',
};
const stringPropNames: NamesType = {
  fw: 'fontWeight',
  bc: 'borderColor',
  bg: 'backgroundColor',
  c: 'color',
  pe: 'pointerEvents',
};
const objProps: { [key: string]: any } = {
  fill: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  abs: {
    position: 'absolute',
  },
  hide: {
    display: 'none',
  },
  fr: {
    flexDirection: 'row',
  },
  js: {
    justifyContent: 'flex-start',
  },
  jc: {
    justifyContent: 'center',
  },
  je: {
    justifyContent: 'flex-end',
  },
  as: {
    alignItems: 'flex-start',
  },
  ac: {
    alignItems: 'center',
  },
  ae: {
    alignItems: 'flex-end',
  },
  tl: {
    textAlign: 'left',
  },
  tc: {
    textAlign: 'center',
  },
  tr: {
    textAlign: 'right',
  },
  icon: {
    fontFamily: 'IPTV-Icons',
  },
  clip: {
    overflow: 'hidden',
  },
};
